<template>
  <v-dialog v-model="data.dialog" persistent max-width="600px">
    <v-card v-if="loading" :color="isDelete ? 'error' : 'info'" dark>
      <v-card-text>
        <span class="font-weight-bold text-white">{{ $t('loading') }}</span>
        <v-progress-linear
          indeterminate
          color="white"
          class="mb-0"
        ></v-progress-linear>
      </v-card-text>
    </v-card>
    <v-card v-if="!loading">
      <v-card-title
        :class="{'form-danger': isDelete, 'form-info': isUpdate, 'form-primary': isCreate}"
      >
        <span v-if="isCreate" class="">{{ $('create') }}</span>
        <span v-if="isUpdate" class="">{{ $('update') }}</span>
        <span v-if="isDelete" class="">{{ $('delete') }}</span>
      </v-card-title>
      <v-card-text>
        <h2 v-if="isDelete && role" class="my-3">
          {{ $('delete_msg', [role.name]) }}
        </h2>
        <v-form
          v-if="!isDelete"
          ref="form"
          v-model="valid"
        >
          <v-container>
            <v-row>
              <v-col
                v-for="locale in locales"
                :key="locale.locale"
                cols="12"
                sm="12"
              >
                <v-text-field
                  v-model="form.description[locale.locale]"
                  v-max-length="200"
                  :label="$('description', [locale.title])"
                  :rules="rules[locale.locale]"
                  required
                >
                  <template v-slot:prepend>
                    <img width="24" height="18" :src="locale.img" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col
                cols="12"
                sm="12"
              >
                <v-select
                  v-model="form.permissions"
                  :items="permissions"
                  :rules="rules.permissions"
                  item-text="description"
                  item-value="name"
                  :label="$('permissions_select')"
                  required
                  multiple
                >
                  <template v-slot:selection="{ item, index }">
                    <span
                      v-if="index === 0"
                      class="grey--text text-caption"
                    >
                      {{ $('permissions_selected', [form.permissions.length]) }}
                    </span>
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="light"
          outlined
          @click="dialogModal(false)"
        >
          {{ $t('form.cancel') }}
        </v-btn>
        <v-btn
          v-if="isCreate"
          color="primary"
          :disabled="!valid"
          @click="submit"
        >
          {{ $t('form.create') }}
        </v-btn>
        <v-btn
          v-if="isUpdate"
          color="info"
          :disabled="!valid"
          @click="submit"
        >
          {{ $t('form.save') }}
        </v-btn>
        <v-btn
          v-if="isDelete"
          color="error"
          @click="submit"
        >
          {{ $t('form.delete') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from '@axios'
import toast from '@/store/toast'
import { locales } from '@/plugins/i18n';

export default {
  name: 'RoleDialog',
  props: {
    data: {
      type: Object,
      default: () => ({
        dialog: false,
        id: null,
        type: 'create',
      }),
    },
    dialogModal: {
      type: Function,
      default: state => {
        this.data.dialog = state
      },
    },
    translationKey: {
      type: String,
      default: () => '',
    },
  },
  emits: ['update:table'],
  setup() {
    return {
      locales,
    }
  },
  data: () => ({
    loading: true,
    valid: false,
    role: null,
    permissions: [],
    form: {
      description: {},
      permissions: [],
    },
    extraRules: {},
  }),
  computed: {
    rules() {
      const rules = {};
      rules.permissions = [
        v => !!v || this.$t('form.required', [this.$t(`${this.translationKey}.permissions`)]),
      ];

      return { ...rules, ...this.extraRules };
    },
    isCreate() {
      return this.data.type === 'create'
    },
    isUpdate() {
      return this.data.type === 'update'
    },
    isDelete() {
      return this.data.type === 'delete'
    },
  },
  watch: {
    data: {
      deep: true,
      immediate: true,
      async handler(val) {
        try {
          this.loading = true;
          if (val.id) {
            const role = await axios.get(`/roles/${val.id}`);
            this.role = role.data.data;
            locales.forEach(locale => {
              this.form.description[locale.locale] = this.role.description[locale.locale];
            });
            this.form.permissions = this.role.permissions;
          }
          if (!val.dialog) {
            this.resetForm();
          }
        } catch (error) {
          //   await toast.dispatch('error', error.response ? error.response.data.message : error.message)
        } finally {
          this.loading = false;
        }
      },
    },
  },
  async mounted() {
    locales.forEach(locale => {
      this.form.description[locale.locale] = '';
      const params = this.$t(`${this.translationKey}.description`, [locale.title]);
      this.extraRules[locale.locale] = [
        v => !!v || this.$t('form.required', [params]),
      ];
    })
    await this.getPermissions()
  },
  methods: {
    async submit() {
      try {
        if (this.isDelete) {
          await axios.delete(`/roles/delete/${this.data.id}`);
          await toast.dispatch('success', this.$t('form.success.delete'))
        }

        // if (this.$refs.form.validate() === false) {
        //   this.$emit('update:table')
        // }
        if (this.isCreate) {
          await axios.post('/roles/create', this.form);
          await toast.dispatch('success', this.$t('form.success.create'))
        } else if (this.isUpdate) {
          await axios.post(`/roles/edit/${this.data.id}`, this.form);
          await toast.dispatch('success', this.$t('form.success.update'))
        }
        this.$emit('update:table')
        this.dialogModal(false)
      } catch (error) {
        //   await toast.dispatch('error', error.response ? error.response.data.message : error.message)
      }
    },
    async getPermissions() {
      const response = await axios.get('permissions')
      this.permissions = response.data.data
    },
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },
    $(key, parameters = null) {
      return this.$t(`${this.translationKey}.modal.${key}`, parameters);
    },
  },
}
</script>

<style scoped>

</style>
